import { Alert, AlertTitle, Box, CircularProgress, Snackbar, SnackbarOrigin } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import './App.css';
import AboutUs from './components/aboutUs/AboutUs';
import Ad from './components/centralComponents/Ad';
import Centered from './components/centralComponents/Centered';
import JoinQRMenuTeam from './components/JoinQRMenuTeam';
import NotFoundPage from './components/NotFoundPage';
import SearchAppBar from './components/appbar/SearchAppBar';
import SearchResultPage from './components/searchResult/SearchResult';
import RestaurantDto from './models/RestaurantDto';
import { useLanguage } from './provider/LanguageContext';
import { useRestaurantData } from './provider/RestaurantDataContext';
import CartPage from './components/cart/CartPage';
import { useSnackbarProps } from './provider/SnackbarContext';
import { useTools } from './tools/Tools';
import Imprint from './components/imprint/Imprint';
import PrivacyPolicy from './components/privacyPolicy/PrivacyPolicy';
import ConfirmationDialog from './components/dialogs/confirmationDialog/ConfirmationDialog';
import LanguageDto from './models/LanguageDto';
import { sendLiveEditorMessage } from './utils/Utils';
import { CATEGORY_URL, EXPIRED_PARAM, HOMEPAGE, ITEM_URL, LIVE_PARAM, MENU_CARD_URL, SEARCH_PARAM, SESSION_ID_PARAM, SESSION_TIMEOUT_WARNING, SUB_CATEGORY_URL, TABLE_PARAM, TARGET_ORIGIN_URL, TOKEN_PARAM } from './utils/Constants';
import { MenuChangeData, ReceivingMessage, RefetchData, SearchData, SelectItemData, TokenData } from './models/message/MessageTypes';
import MenuCard from './components/menuCard/mainPage/MenuCard';
import CategoryPage from './components/menuCard/categoryPage/CategoryPage';
import ItemPage from './components/menuCard/itemPage/ItemPage';
import OpeningHoursPage from './components/openingHours/OpeningHoursPage';
import ContactPage from './components/contact/ContactPage';
import AllergenPage from './components/allergens/AllergenPage';
import AllItemsPage from './components/allItems/AllItemsPage';

function useQueryParams() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const userLocale = window.navigator.languages[0] || window.navigator.language;

function App() {
  const { t } = useTranslation();
  const { snackbarProps, setSnackbarProps } = useSnackbarProps();
  const { setRestaurantData, setTable, table: storedTableId, restaurantData, setLive: setLiveGlobal, setSessionId, sessionId, setExpired, expired } = useRestaurantData();
  const { setLanguage, language } = useLanguage();
  const queryParams = useQueryParams();
  const { handleToken } = useTools();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const [live, setLive] = useState(false);
  const [errorLanguage, setErrorLanguage] = useState<{ errorMessage: string } | undefined>(undefined);
  const tokenValidationPending = useRef<boolean>(false);
  const [sessionTimedOutDialog, setSessionTimedOutDialog] = useState(false);
  const [sessionTimedOutWarningDialog, setSessionTimedOutWarningDialog] = useState(false);
  const [firstRenderDone, setFirstRenderDone] = useState(false);
  const [timeTillTimeout, setTimeTillTimeout] = useState(0);
  const environment = process.env.REACT_APP_ENVIRONMANET ?? '';
  const snackbarPosition: SnackbarOrigin = {
    vertical: "bottom", horizontal: "right"
  }

  useEffect(() => {
    setLiveGlobal(live);
  }, [live, setLiveGlobal]);

  const handleCloseSnackbar = () => {
    setSnackbarProps({ ...snackbarProps, open: false });
  };

  useEffect(() => {
    const live = queryParams.get(LIVE_PARAM) === "true";
    const token = queryParams.get(TOKEN_PARAM);
    if (live && token && (token?.length ?? 0) > 0) {
      const checkIfLiveIsValid = async () => {
        tokenValidationPending.current = true;
        const isTokenValid = await handleToken(token ?? '');
        setLive(isTokenValid);
        tokenValidationPending.current = false;
      }
      if (!tokenValidationPending.current) {
        checkIfLiveIsValid();
      }
    } else {
      setLive(false);
    }
    const sessionIdParam = queryParams.get(SESSION_ID_PARAM) ?? sessionId ?? '';
    setSessionId(sessionIdParam);
    const expiredParam = new Date(queryParams.get(EXPIRED_PARAM) ?? expired ?? Date.now());
    setExpired(expiredParam);
    setTimeTillTimeout(expiredParam.getTime() - Date.now());
    const table = queryParams.get(TABLE_PARAM);
    if (table) {
      setTable(Number.parseInt(table));
    }
    console.log('pathname', pathname);
    if (environment === 'PRD') {
      navigate(pathname, { relative: 'route' });
    }
    let expiredTimeout: NodeJS.Timeout;
    let expiredWarningTimeout: NodeJS.Timeout;
    if (sessionIdParam.length > 0 && expiredParam.getTime() > Date.now()) {
      expiredWarningTimeout = setTimeout(() => {
        setSessionTimedOutWarningDialog(true);
      }, expiredParam.getTime() - Date.now() - SESSION_TIMEOUT_WARNING * 60 * 1000);
      expiredTimeout = setTimeout(() => {
        setSessionTimedOutWarningDialog(false);
        setSessionTimedOutDialog(true);
        setTable(0);
        setSessionId('');
      }, expiredParam.getTime() - Date.now())
    }
    setFirstRenderDone(true);
    return () => {
      clearTimeout(expiredWarningTimeout);
      clearTimeout(expiredTimeout);
      setFirstRenderDone(false);
    }
  }, []);

  const path = useMemo(() => pathname.substring(pathname.indexOf(HOMEPAGE + '/') + (HOMEPAGE + '/').length), [pathname]);

  const { isLoading, error, data, refetch } = useQuery<RestaurantDto, Error>([`/restaurants/${path.substring(0, path.indexOf('/'))}?lang=${language?.language ?? 'de'}&live=${live}${sessionId && sessionId.length > 0 ? `&sessionId=${sessionId}` : ''}`], {
    enabled: language?.language !== undefined && firstRenderDone && !tokenValidationPending.current,
    onSuccess(data) {
      setRestaurantData(data);
      if (!language?.id) {
        setLanguage(data.languages.filter(lang => lang.language === language?.language)[0]);
      }
      if (data.tables.filter(table => table.id === storedTableId).length === 0) {
        setTable(0);
      }
    },
    onError: (error: any) => {
      console.error('error fetching restaurant data', error);
    }
  });

  useEffect(() => {
    if (window.location !== window.parent.location && !tokenValidationPending.current && firstRenderDone) {
      console.log(`You changed the page to: ${pathname}`, queryParams, queryParams.toString());
      sendLiveEditorMessage({ type: 'editorEvent', data: { url: pathname, refetch: false, queryParams: queryParams.toString() } });
    }
  }, [pathname, firstRenderDone, queryParams]);

  useEffect(() => {
    if (window.location !== window.parent.location && !isLoading && firstRenderDone && !tokenValidationPending.current) {
      sendLiveEditorMessage({ type: 'loaded', data: { url: pathname } });
    }
  }, [isLoading, firstRenderDone, pathname]);

  useEffect(() => {
    if (window.location !== window.parent.location) {
      sendLiveEditorMessage({ type: 'rendered', data: { url: pathname } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleNewToken = async (token: string) => {
      const tokenValid = await handleToken(token);
      if (!tokenValid) {
        setLive(false);
      } 
      return tokenValid;
    }
    const handleMessage = async (event: MessageEvent<ReceivingMessage>) => {
      if (event.origin !== TARGET_ORIGIN_URL || !firstRenderDone) return;
      console.log("QR-Menu targetOriginUrl:", TARGET_ORIGIN_URL);
      console.log('QR-Menu event', event, live);
      const receivedData = event.data.data;
      const messageType = event.data.type;
      if (!receivedData) {
        return;
      }
      const tokenValid = await handleNewToken(receivedData.token);
      if (!tokenValid) {
        console.error('invalid token for command');
        return;
      }
      if (event.data.type === 'health') {
        sendLiveEditorMessage({ type: 'health', data: { url: pathname, status: 200 } });
      } else if (event.data.type === 'changeMenu') {
        const menuChangeData = receivedData as MenuChangeData;
        const baseMenuPath = `${HOMEPAGE}/${path.substring(0, path.indexOf('/'))}`;
        const queryParams = new URLSearchParams();;
        if (menuChangeData.initData?.allItems) {
          queryParams.set('allItems', 'true');
        }
        navigate(`${baseMenuPath}/${menuChangeData.newMenu}${queryParams.toString().length > 0 ? '?' + queryParams.toString() : ''}`);
      } else if (messageType === 'selectItem') {
        const selectItemData = receivedData as SelectItemData;
        const selectedItem = selectItemData.selectedItem;
        const selectedItemUrl = selectedItem === 'category' ? CATEGORY_URL : selectedItem === 'subCategory' ? SUB_CATEGORY_URL : selectedItem === 'item' ? ITEM_URL : '';
        if (pathname.includes(selectedItemUrl)) {
          console.log('change url to 1', pathname.substring(0, pathname.indexOf(selectedItemUrl) + selectedItemUrl.length) + '/' + selectItemData.id);
          navigate(pathname.substring(0, pathname.indexOf(selectedItemUrl) + selectedItemUrl.length) + '/' + selectItemData.id);
        } else {
          if (!pathname.includes(MENU_CARD_URL)) {
            // if the current page is not in the menu but search or somewhere else
            const baseMenuPath = `${HOMEPAGE}/${path.substring(0, path.indexOf('/'))}/${MENU_CARD_URL}`;
            let addingPath = "";
            if (selectItemData.categoryId) {
              addingPath = '/' + CATEGORY_URL + '/' + selectItemData.categoryId;
            }
            if (selectItemData.subCategoryId) {
              addingPath += '/' + SUB_CATEGORY_URL + '/' + selectItemData.subCategoryId;
            }
            if (selectedItem === 'item') {
              addingPath += '/' + ITEM_URL + '/' + selectItemData.id;
            }
            navigate(baseMenuPath + addingPath);
          } else {
            console.log('change url to 2', pathname + '/' + selectedItemUrl + '/' + selectItemData.id);
            navigate(pathname + (pathname.endsWith('/') ? '' : '/') + selectedItemUrl + '/' + selectItemData.id);
          }
        }
      } else if (live && messageType === 'goBackFromItem') {
        let goBack = 2;
        if (!pathname.includes(ITEM_URL) && pathname.includes(SUB_CATEGORY_URL)) {
          goBack += 2;
        }
        // goBack * 2 becaue url is always item/{itemId} for example so going back from item needs to be done twice
        navigate(new Array(goBack).fill('../').join(''), { relative: 'path' });
      } else if (live && messageType === 'refetch') {
        const refetchData = receivedData as RefetchData;
        if (!refetchData.refetch) return;
        console.log('QR-Menu data', receivedData);
        console.log("QR-Menu  live refetching");
        refetch();
      } else if (event.data.type === 'token') {
        //TODO && !live
        const tokenData = receivedData as TokenData;
        console.log('QR-Menu token', receivedData);
        await handleNewToken(tokenData.token);
      } else if (event.data.type === 'search') {
        const searchData = receivedData as SearchData;
        searchParams.set(SEARCH_PARAM, searchData.search);
        setSearchParams(new URLSearchParams(searchParams));
      }
    }
    window.addEventListener("message", handleMessage);
    // 👇️ remove the event listener when component unmounts
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleToken, live, navigate, pathname, refetch, firstRenderDone, setSearchParams, searchParams, path]);

  useQuery<LanguageDto[], Error>([`/restaurants/${path.substring(0, path.indexOf('/'))}/languages?live=${live}${sessionId && sessionId.length > 0 ? `&sessionId=${sessionId}` : ''}`], {
    enabled: language?.language === undefined && firstRenderDone && !tokenValidationPending.current,
    retry: 5,
    onSuccess(data) {
      if (!Array.isArray(data)) {
        setErrorLanguage(data);
        return;
      }
      setErrorLanguage(undefined);
      const filteredLanguages = data.filter(lang => lang.language.toLowerCase() === userLocale.split('-')[0].toLowerCase());
      if (filteredLanguages.length > 0) {
        setLanguage({ ...filteredLanguages[0] });
      } else {
        setLanguage({ ...data[0] });
      }
    }
  });

  useEffect(() => {
    let interval: NodeJS.Timer | undefined;
    if (expired && expired.getTime() > Date.now()) {
      interval = setInterval(() => {
        setTimeTillTimeout(expired.getTime() - Date.now());
      }, 1000);
    }

    return () => {
      if (interval)
        clearInterval(interval);
    };
  }, [expired]);

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', maxHeight: '100vh', justifyContent: 'stretch' }}>
        <SearchAppBar />
        <Box sx={{
          flexGrow: 2, display: 'flex', overflowY: 'auto', maxHeight: '100%',
          backgroundImage: `url(${TARGET_ORIGIN_URL}/images/qr-menu/default/Background.webp)`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'scroll',
        }}>
          <Centered>
            {!errorLanguage && (isLoading || !(language?.language !== undefined && firstRenderDone && !tokenValidationPending.current)) &&
              <Box textAlign="center" mt={2}>
                <CircularProgress />
              </Box>
            }
            {(error || errorLanguage) &&
              <Alert severity="error">
                <>
                  <AlertTitle>{t('error')}</AlertTitle>
                  {error ? (error?.message) : (errorLanguage?.errorMessage)}
                </>
              </Alert>
            }
            {!data?.id && !error && !isLoading && (language?.language !== undefined && firstRenderDone && !tokenValidationPending.current) &&
              <Alert severity="error">
                <>
                  <AlertTitle>{t('error')}</AlertTitle>
                  {(data as any)?.errorMessage}
                </>
              </Alert>}
            {
              data?.id &&
              <Routes>
                <Route path={`/${HOMEPAGE}/:restaurantId`}>
                  <Route path={`${MENU_CARD_URL}`} element={<MenuCard categories={data.categories} />} />
                  <Route path={`${MENU_CARD_URL}/${CATEGORY_URL}/:mainCategory`} element={<CategoryPage />} />
                  <Route path={`${MENU_CARD_URL}/${CATEGORY_URL}/:mainCategory/${SUB_CATEGORY_URL}/:subCategory/`} element={<CategoryPage />} />
                  <Route path={`${MENU_CARD_URL}/${CATEGORY_URL}/:mainCategory/${SUB_CATEGORY_URL}/:subCategory/${ITEM_URL}/:itemDetail`} element={<ItemPage />} />
                  <Route path="openingHours" element={<OpeningHoursPage />} />
                  {restaurantData.qrMenuProperties?.showAboutUs && <Route path="about-us" element={<AboutUs />} />}
                  <Route path="search" element={<SearchResultPage />} />
                  <Route path="join-qrmenu" element={<JoinQRMenuTeam />} />
                  <Route path="contact" element={<ContactPage />} />
                  <Route path="allergens" element={<AllergenPage />} />
                  <Route path="cart" element={<CartPage />} />
                  <Route path="imprint" element={<Imprint />} />
                  <Route path="privacy-policy" element={<PrivacyPolicy />} />
                  {live && <Route path="all-items" element={<AllItemsPage />} />}
                </Route>
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            }
          </Centered>
        </Box>
        <Ad style={{ position: 'sticky', top: 0 }} />
      </div>
      <Snackbar
        open={snackbarProps.open}
        autoHideDuration={snackbarProps.timeout}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ ...snackbarPosition }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbarProps.severity} variant="filled" sx={{ width: '100%', whiteSpace: 'pre-line' }}>
          {snackbarProps.snackbarText}
        </Alert>
      </Snackbar>
      <ConfirmationDialog
        dialogTitle={t('sessionWillExpireTitle') || 'sessionWillExpireTitle'}
        dialogText={t('sessionWillExpireDescription', { minutes: Math.floor(timeTillTimeout / (1000 * 60.0)).toFixed(0), seconds: Math.floor((timeTillTimeout - (60 * 1000 * Number.parseInt(Math.floor(timeTillTimeout / (1000 * 60.0)).toFixed(0)))) / 1000).toFixed(0) }) || 'sessionWillExpireDescription'}
        confirmButtonText={t('okay') || 'okay'}
        onConfirmation={() => setSessionTimedOutWarningDialog(false)}
        opened={sessionTimedOutWarningDialog} />
      <ConfirmationDialog
        dialogTitle={t('sessionExpiredTitle') || 'sessionExpiredTitle'}
        dialogText={t('sessionExpirdeDescription') || 'sessionExpirdeDescription'}
        confirmButtonText={t('okay') || 'okay'}
        onConfirmation={() => setSessionTimedOutDialog(false)}
        opened={sessionTimedOutDialog} />
    </>
  );
}

export default App;
